import './projectListWithPairs.scss';

import { motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, matchPath, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import { tailwindConfig } from 'tailwind-config';

import { useThemeColors } from '@/shared/theme';

import { useArbitrageConfigs } from '@/entities/arbitrage';
import { buildSymbolStatusKey, useCexSymbolStatuses } from '@/entities/cex';

import { IDex } from 'api/apiDictionary/models';
import { AddPairModal, AddSolanaPairModal, SelectPairDexModal, TradingPairModal } from 'modals';
import { AddArbitrageModal } from 'modals/AddArbitrageModal/AddArbitrageModal';
import PlatformIcon from 'shared/components/PlatformIcon';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedSelector } from 'store';
import { ICexPair, IDexPair } from 'types/pairs';
import { IProject } from 'types/project';
import { RadioBox } from 'ui/RadioBox/RadioBox';
import { TableAction, TableActionIcon } from 'ui/Table';
import { Tooltip } from 'ui/Tooltip';
import { ECexName, EExchange, ENetwork, isSolanaPoolDex } from 'web3';

import NetworkIcon from './NetworkIcon';

import { PlusIcon, SelectArrow } from 'assets/icons';

type ICommonPair = (IDexPair | ICexPair) & { exchange: EExchange };

type ProjectListWithPairsProps = {
  id: string;
  type: 'active' | 'archive';
  project: IProject;
  arbitrageIds: number[];
  balancerTasksIds: number[];
  getArbitrages: () => void;
  selectedProjectId: number | null;
  onSelectProject: (id: number) => void;
};

const capitalizeWord = (value: string) => (value ? value[0].toUpperCase() + value.slice(1) : '');

const formatExchangeName = (value: string) => {
  const [rawName, chain] = value.split(':');

  const name = rawName
    .split('_')
    .map(part => (/^v\\d+$/i.test(part) ? part.toUpperCase() : capitalizeWord(part)))
    .join(' ');

  return chain ? `${name} (${chain.toUpperCase()})` : name;
};

export const ProjectListWithPairs = ({
  id,
  type,
  project,
  arbitrageIds,
  balancerTasksIds,
  getArbitrages,
  selectedProjectId,
  onSelectProject,
}: ProjectListWithPairsProps) => {
  const { pathname } = useLocation();
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);
  const palette = useThemeColors();

  const projectIsSelected: boolean = useMemo(
    () => project.id === selectedProjectId,
    [project.id, selectedProjectId],
  );

  const [tradingType, setTradingType] = useState<EExchange>(EExchange.cex);
  const [dex, setDex] = useState<IDex | undefined>(undefined);

  const [tradingPairWindowOpened, setTradingPairWindowOpened] = useState(false);
  const [addPairWindowOpened, setAddPairWindowOpened] = useState(false);
  const [addSolanaPairWindowOpened, setAddSolanaPairWindowOpened] = useState(false);
  const [selectPairDexModalOpened, setSelectPairDexModalOpened] = useState(false);
  const [addArbitrageWindowOpened, setAddArbitrageWindowOpened] = useState(false);

  const selectedSolanaPoolDex =
    dex?.network === ENetwork.solana && isSolanaPoolDex(dex.dex) ? dex.dex : undefined;

  const openTradingPairWindow = useCallback(() => {
    setTradingPairWindowOpened(true);
  }, []);

  const closeTradingPairWindow = useCallback(() => {
    setTradingPairWindowOpened(false);
  }, []);

  const openArbitrageWindow = useCallback(() => {
    setAddArbitrageWindowOpened(true);
  }, []);

  const closeArbitrageWindow = useCallback(() => {
    setAddArbitrageWindowOpened(false);
  }, []);

  const openAddPairWindow = useCallback(() => {
    setAddPairWindowOpened(true);
    setTradingPairWindowOpened(false);
    setSelectPairDexModalOpened(false);
  }, []);

  const openAddSolanaPairWindow = useCallback(() => {
    setAddSolanaPairWindowOpened(true);
    setTradingPairWindowOpened(false);
    setSelectPairDexModalOpened(false);
  }, []);

  const openSelectedDexPairWindow = useCallback(() => {
    if (selectedSolanaPoolDex) {
      openAddSolanaPairWindow();
      return;
    }

    if (dex?.network === ENetwork.solana) {
      return;
    }

    openAddPairWindow();
  }, [dex, openAddPairWindow, openAddSolanaPairWindow, selectedSolanaPoolDex]);

  const openSelectPairDex = useCallback(() => {
    setDex(undefined);
    setSelectPairDexModalOpened(true);
    setTradingPairWindowOpened(false);
  }, []);

  const closeSelectPairDex = useCallback(() => {
    setSelectPairDexModalOpened(false);
  }, []);

  const closeAddPairWindow = useCallback(() => {
    setAddPairWindowOpened(false);
  }, []);

  const closeAddSolanaPairWindow = useCallback(() => {
    setAddSolanaPairWindowOpened(false);
  }, []);

  const projectAllPairsIsActive = useMemo(
    () => Boolean(matchPath(`project/${project.id}`, pathname)),
    [pathname, project],
  );

  const pairs = useMemo(
    () =>
      [...project.dexPairs, ...project.cexPairs, ...project.solPairs]
        .sort((a, b) => {
          const createdAtA = new Date(a.created_at).valueOf();
          const createdAtB = new Date(b.created_at).valueOf();

          return createdAtA - createdAtB;
        })
        .map(el => {
          if ('cex' in el) return { ...el, exchange: EExchange.cex };
          else if ('dex' in el) {
            if (el.network === 'solana') return { ...el, exchange: EExchange.sol };
            return { ...el, exchange: EExchange.dex };
          }
        }) as ICommonPair[],
    [project],
  );

  function getPairPath(exchange: EExchange): string {
    switch (exchange) {
      case EExchange.cex:
        return ROUTE_PATHS.cexPair;
      case EExchange.dex:
        return ROUTE_PATHS.dexPair;
      case EExchange.sol:
        return ROUTE_PATHS.solPair;
      default:
        return '';
    }
  }

  const cexStatusRequests = useMemo(() => {
    if (!projectIsSelected) {
      return [];
    }

    return (project.cexPairs ?? [])
      .map(pair => {
        const base =
          (pair as any).token_base?.symbol ??
          (typeof pair.symbol === 'string' ? pair.symbol.split('/')[0] : undefined);
        const quote =
          (pair as any).token_quote?.symbol ??
          (typeof pair.symbol === 'string' ? pair.symbol.split('/')[1] : undefined);

        if (!base || !quote) return null;

        return {
          cex: (pair as any).cex as ECexName,
          base,
          quote,
        };
      })
      .filter(Boolean) as Array<{ cex: ECexName; base: string; quote: string }>;
  }, [project.cexPairs, projectIsSelected]);

  const { data: symbolStatuses } = useCexSymbolStatuses({
    items: cexStatusRequests,
    enabled: projectIsSelected,
  });

  const { data: arbitrageConfigs } = useArbitrageConfigs(
    { projectId: project.id },
    {
      enabled: isAdmin && projectIsSelected,
      gcTime: Infinity,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const arbitrageTickerById = useMemo(() => {
    if (!arbitrageConfigs || arbitrageConfigs.length === 0) {
      return new Map<number, string>();
    }

    const cexSymbolById = new Map(project.cexPairs.map(pair => [pair.id, pair.symbol]));
    const dexSymbolById = new Map(
      [...project.dexPairs, ...project.solPairs].map(pair => [pair.id, pair.symbol]),
    );

    const result = new Map<number, string>();

    for (const config of arbitrageConfigs) {
      const candidates = [...(config.pairs_cex ?? []), ...(config.pairs_dex ?? [])];
      for (const pair of candidates) {
        const symbol = cexSymbolById.get(pair.pair_id) ?? dexSymbolById.get(pair.pair_id);

        if (symbol) {
          result.set(config.id, symbol);
          break;
        }
      }
    }

    return result;
  }, [arbitrageConfigs, project.cexPairs, project.dexPairs, project.solPairs]);

  return (
    <>
      <div className="mm-sidebar__projects__project" key={project.id} id={id}>
        <div
          className="mm-sidebar__projects__project__header"
          onClick={() => onSelectProject(project.id)}
        >
          <motion.div
            animate={{
              color: projectIsSelected ? palette.primary : undefined,
            }}
            className="mm-sidebar__projects__project__name"
          >
            <span>{project.name}</span>
          </motion.div>
          <motion.div
            animate={{ rotate: projectIsSelected ? 180 : 0 }}
            className="mm-sidebar__projects__project__select-icon"
          >
            <SelectArrow
              color={projectIsSelected ? tailwindConfig.theme.colors.primary : undefined}
            />
          </motion.div>
        </div>
        <motion.div
          initial={{ height: 0, overflow: 'hidden' }}
          animate={{
            height: projectIsSelected ? 'auto' : 0,
            marginBottom: projectIsSelected ? '1rem' : '0rem',
          }}
          className="mm-sidebar__projects__project__pairs"
        >
          <NavLink to={generatePath(ROUTE_PATHS.project, { id: project.id.toString() })}>
            <RadioBox checked={projectAllPairsIsActive} text="All pairs" />
          </NavLink>

          {pairs.length === 0 && (
            <span className="mm-sidebar__projects__project__no-info">
              There is no existing pairs. Create first one!
            </span>
          )}

          {pairs.map(pair => {
            const exchangeName =
              'dex' in pair || 'cex' in pair
                ? formatExchangeName('cex' in pair ? pair.cex : pair.dex)
                : '';

            let displaySymbol = pair.symbol;

            if ('cex' in pair) {
              const base =
                (pair as any).token_base?.symbol ??
                (typeof pair.symbol === 'string' ? pair.symbol.split('/')[0] : undefined);
              const quote =
                (pair as any).token_quote?.symbol ??
                (typeof pair.symbol === 'string' ? pair.symbol.split('/')[1] : undefined);

              if (base && quote) {
                const key = buildSymbolStatusKey(pair.cex, quote, base);
                const isSt = symbolStatuses?.[key]?.isSt;
                if (isSt) {
                  displaySymbol = `${pair.symbol} (ST)`;
                }
              }
            }

            const pairLink = (
              <NavLink
                to={generatePath(getPairPath(pair.exchange), {
                  projectId: project.id.toString(),
                  pairId: pair.id.toString(),
                })}
              >
                <RadioBox
                  checked={Boolean(
                    matchPath(
                      generatePath(getPairPath(pair.exchange), {
                        projectId: project.id.toString(),
                        pairId: pair.id.toString(),
                      }),
                      pathname,
                    ),
                  )}
                  text={displaySymbol}
                  pairIcon={
                    <div className="project-pair-container">
                      {'dex' in pair && <NetworkIcon network={pair.network} />}
                      <PlatformIcon
                        platform={'cex' in pair ? pair.cex : pair.dex}
                        isIcon
                        className="mm-sidebar__projects__project__trading-pair-icon"
                      />
                    </div>
                  }
                />
              </NavLink>
            );

            return (
              <div key={pair.created_at} className="mm-sidebar__projects__project__trading-pair">
                <Tooltip
                  trigger={pairLink}
                  content={exchangeName}
                  options={{ placement: 'right', offset: 8 }}
                  inline
                />
              </div>
            );
          })}

          {isAdmin &&
            arbitrageIds.map(id => {
              const ticker = arbitrageTickerById.get(id);

              return (
                <div key={id} className="mm-sidebar__projects__project__trading-pair">
                  <NavLink
                    to={generatePath(ROUTE_PATHS.arbitrage, {
                      projectId: project.id.toString(),
                      arbitrageId: id.toString(),
                    })}
                  >
                    <RadioBox
                      checked={Boolean(
                        matchPath(
                          generatePath(ROUTE_PATHS.arbitrage, {
                            projectId: project.id.toString(),
                            arbitrageId: id.toString(),
                          }),
                          pathname,
                        ),
                      )}
                      text={`Arbitrage #${id} (${ticker ?? 'Stopped'})`}
                    />
                  </NavLink>
                </div>
              );
            })}

          {isAdmin &&
            balancerTasksIds.map(id => (
              <div key={id} className="mm-sidebar__projects__project__trading-pair">
                <NavLink
                  to={generatePath(ROUTE_PATHS.balancer, {
                    projectId: project.id.toString(),
                    balancerId: id.toString(),
                  })}
                >
                  <RadioBox
                    checked={Boolean(
                      matchPath(
                        generatePath(ROUTE_PATHS.balancer, {
                          projectId: project.id.toString(),
                          balancerId: id.toString(),
                        }),
                        pathname,
                      ),
                    )}
                    text={`Balancer ${id}`}
                  />
                </NavLink>
              </div>
            ))}

          {isAdmin && type === 'active' && (
            <TableAction
              iconLeft={<TableActionIcon icon={PlusIcon} sideBar />}
              text="Add new pair"
              onClick={openTradingPairWindow}
              customClassName="mm-sidebar__projects__project__add-pair"
            />
          )}

          {isAdmin && type === 'active' && (
            <NavLink
              to={generatePath(ROUTE_PATHS.balancerCreate, {
                projectId: project.id.toString(),
              })}
            >
              <TableAction
                iconLeft={<TableActionIcon icon={PlusIcon} sideBar />}
                text="Add balancer"
                customClassName="mm-sidebar__projects__project__add-pair"
              />
            </NavLink>
          )}
        </motion.div>

        {isAdmin && (
          <motion.div
            initial={{ height: 0, overflow: 'hidden' }}
            animate={{
              height: projectIsSelected ? 'auto' : 0,
              marginBottom: projectIsSelected ? '1rem' : '0rem',
            }}
            className="mm-sidebar__projects__project__pairs"
          >
            {pairs.length === 0 && (
              <span className="mm-sidebar__projects__project__no-info">
                There is no existing pairs. Create first one!
              </span>
            )}
            {type === 'active' && pairs.length !== 0 && (
              <TableAction
                iconLeft={<TableActionIcon icon={PlusIcon} sideBar />}
                text="Add arbitrage"
                onClick={openArbitrageWindow}
                customClassName="mm-sidebar__projects__project__add-pair"
              />
            )}
          </motion.div>
        )}
      </div>

      {tradingPairWindowOpened && (
        <TradingPairModal
          openAddPairWindow={openAddPairWindow}
          openSelectPairDex={openSelectPairDex}
          onClose={closeTradingPairWindow}
          typeTradingPair={tradingType}
          setTypeTradingPair={setTradingType}
        />
      )}

      {selectPairDexModalOpened && (
        <SelectPairDexModal
          dex={dex}
          setDex={setDex}
          openSelectedDexPairWindow={openSelectedDexPairWindow}
          onClose={closeSelectPairDex}
        />
      )}

      {addPairWindowOpened && (
        <AddPairModal
          onOpen={openAddPairWindow}
          onClose={closeAddPairWindow}
          projectId={project.id}
          tradingType={tradingType}
          dex={dex}
        />
      )}

      {addSolanaPairWindowOpened && selectedSolanaPoolDex && (
        <AddSolanaPairModal
          dex={selectedSolanaPoolDex}
          projectId={project.id}
          onClose={closeAddSolanaPairWindow}
        />
      )}

      {addArbitrageWindowOpened && (
        <AddArbitrageModal
          onClose={closeArbitrageWindow}
          onSuccess={getArbitrages}
          projectId={project.id}
        />
      )}
    </>
  );
};
